<template>
  <div
    v-if="isAutoApproved"
    data-testid="ds-attendance-auto-approved-banner"
    class="flex flex-col gap-2 rounded-lg border border-sucess bg-green-50 p-3 text-left"
  >
    <span
      class="w-fit rounded bg-sucess px-2 py-[2px] text-[11px] font-bold uppercase leading-4 tracking-wide text-white"
    >
      {{ APPROVAL_MODE_LABELS.AUTOMATIC }}
    </span>
    <p class="text-sm leading-[18px] text-textPrimary">{{ bannerText }}</p>
    <div class="mt-1 flex flex-col gap-2 sm:flex-row">
      <OutlineButton
        text="Ver critérios"
        data-testid="ds-attendance-auto-approved-criteria-button"
        width="160px"
        height="36px"
        @click="emit('showCriteria')"
      />
      <OutlineButton
        v-if="canReopen"
        text="Reabrir para auditoria"
        data-testid="ds-attendance-auto-approved-reopen-button"
        width="210px"
        height="36px"
        :disabled="reopening"
        @click="emit('reopen')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Fase 2 do fluxo de auditoria (spec 2026-09-11, §6) — o estado "Aprovado automaticamente"
 * na tela de auditoria: banner verde com a contagem de plantões que conferiram, "Ver
 * critérios" e "Reabrir para auditoria" (o mesmo `revert-validation` do fluxo manual).
 *
 * `approvalMode` ausente é backend anterior à fase 2: nada é renderizado e a tela segue
 * exatamente como hoje.
 */
import { computed } from 'vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import { APPROVAL_MODE_LABELS } from '@/constants/ds-attendance-auto-approval.constants'
import type { DSAttendanceApprovalMode } from '@/Network/Types/Responses/DsAttendanceAuditResponses'

const props = withDefaults(
  defineProps<{
    approvalMode?: DSAttendanceApprovalMode | null
    /** `true` só quando o container está de fato APROVADO — o chip não vale em outro status. */
    isApproved?: boolean
    /** N do banner: `prefillTotalLines` e, na falta dele, `expectedDoctorStations`. */
    lineCount?: number | null
    /** Permissão de reverter validação — mesma que já gateia "Reverter validação". */
    canReopen?: boolean
    reopening?: boolean
  }>(),
  { isApproved: false, canReopen: false, reopening: false }
)

const emit = defineEmits<{
  showCriteria: []
  reopen: []
}>()

const isAutoApproved = computed(() => props.isApproved && props.approvalMode === 'AUTOMATIC')

const bannerText = computed(() => {
  const total = props.lineCount ?? 0
  if (total <= 0) {
    return 'Todos os plantões previstos conferem com a folha dentro da tolerância.'
  }
  const plantoes = total === 1 ? 'plantão previsto confere' : 'plantões previstos conferem'
  return `Todos os ${total} ${plantoes} com a folha dentro da tolerância.`
})
</script>
